import React, { useState } from 'react';
import { ChevronLeft, ChevronRight, Eye, Code, Zap, Copy, Check } from 'lucide-react';

const PromptDiffViewer = ({ 
  versions, 
  selectedVersions = [], 
  onVersionSelect,
  compact = false 
}) => {
  const [viewMode, setViewMode] = useState('side-by-side'); // 'side-by-side' or 'unified'
  const [showFormatted, setShowFormatted] = useState(true);
  const [copiedStates, setCopiedStates] = useState({});

  const handleCopy = async (content, versionId) => {
    try {
      await navigator.clipboard.writeText(content);
      setCopiedStates(prev => ({ ...prev, [versionId]: true }));
      setTimeout(() => {
        setCopiedStates(prev => ({ ...prev, [versionId]: false }));
      }, 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  // Simple diff algorithm for highlighting changes
  const computeDiff = (text1, text2) => {
    const lines1 = text1.split('\n');
    const lines2 = text2.split('\n');
    const maxLines = Math.max(lines1.length, lines2.length);
    
    const diff = [];
    for (let i = 0; i < maxLines; i++) {
      const line1 = lines1[i] || '';
      const line2 = lines2[i] || '';
      
      if (line1 === line2) {
        diff.push({ type: 'equal', content: line1, lineNumber: i + 1 });
      } else if (!line1) {
        diff.push({ type: 'added', content: line2, lineNumber: i + 1 });
      } else if (!line2) {
        diff.push({ type: 'removed', content: line1, lineNumber: i + 1 });
      } else {
        diff.push({ type: 'modified', content1: line1, content2: line2, lineNumber: i + 1 });
      }
    }
    return diff;
  };

  const formatPromptContent = (content) => {
    if (!showFormatted) return content;
    
    // Simple formatting for better readability
    return content
      .replace(/(\n|^)(System:|Assistant:|User:|Human:)/g, '\n**$2**')
      .replace(/(\n|^)(Input:|Output:|Example:)/g, '\n*$2*')
      .replace(/\{([^}]+)\}/g, '`{$1}`'); // Highlight variables
  };

  const getVersionColor = (version) => {
    if (version.is_control) return 'border-celadon-400 bg-celadon-50';
    if (version.version_name?.includes('Auto-tuned')) return 'border-gray-400 bg-gray-50';
    return 'border-gray-300 bg-gray-50';
  };

  // Simple SVG icons to replace emoji performance indicators
  const HighPerformanceIcon = () => (
    <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 24 24">
      <circle cx="12" cy="12" r="10" />
    </svg>
  );

  const MediumPerformanceIcon = () => (
    <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 24 24">
      <circle cx="12" cy="12" r="8" />
    </svg>
  );

  const LowPerformanceIcon = () => (
    <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 24 24">
      <circle cx="12" cy="12" r="6" />
    </svg>
  );

  const NoDataIcon = () => (
    <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 24 24">
      <circle cx="12" cy="12" r="4" />
    </svg>
  );

  const getPerformanceIndicator = (version) => {
    const score = parseFloat(version.avg_auto_eval_score) || 0;
    if (score >= 0.8) return { color: 'text-celadon-600', icon: <HighPerformanceIcon />, label: 'High Performance' };
    if (score >= 0.6) return { color: 'text-gray-600', icon: <MediumPerformanceIcon />, label: 'Medium Performance' };
    if (score > 0) return { color: 'text-gray-500', icon: <LowPerformanceIcon />, label: 'Low Performance' };
    return { color: 'text-gray-400', icon: <NoDataIcon />, label: 'No Data' };
  };

  if (!versions || versions.length === 0) {
    return (
      <div className="text-center py-8 text-gray-500">
        <Code className="h-12 w-12 mx-auto mb-4 text-gray-300" />
        <p>No prompt versions available for comparison</p>
      </div>
    );
  }

  if (compact) {
    return (
      <div className="space-y-2">
        {versions.map((version) => (
          <div 
            key={version.id} 
            className={`p-3 border rounded-lg cursor-pointer transition-all ${
              selectedVersions.includes(version.id) 
                ? getVersionColor(version)
                : 'border-gray-200 hover:border-gray-300'
            }`}
            onClick={() => onVersionSelect && onVersionSelect(version.id)}
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-2">
                <span className="font-medium">{version.version_name}</span>
                {version.is_control && (
                  <span className="px-2 py-1 text-xs bg-celadon-100 text-celadon-700 rounded-full">Control</span>
                )}
              </div>
              <div className="text-sm text-gray-500">
                v{version.version_number}
              </div>
            </div>
            <div className="text-sm text-gray-600 mt-1 truncate">
              {version.content?.substring(0, 100)}...
            </div>
          </div>
        ))}
      </div>
    );
  }

  const selectedVersionObjects = versions.filter(v => selectedVersions.includes(v.id));

  return (
    <div className="space-y-4">
      {/* Controls */}
      <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
        <div className="flex items-center space-x-4">
          <div className="text-sm font-medium text-gray-700">View Mode:</div>
          <div className="flex rounded-lg border border-gray-300 bg-white">
            <button
              onClick={() => setViewMode('side-by-side')}
              className={`px-3 py-2 text-sm rounded-l-lg transition-colors ${
                viewMode === 'side-by-side' 
                  ? 'bg-celadon-500 text-white' 
                  : 'text-gray-700 hover:bg-gray-50'
              }`}
            >
              Side by Side
            </button>
            <button
              onClick={() => setViewMode('unified')}
              className={`px-3 py-2 text-sm rounded-r-lg transition-colors ${
                viewMode === 'unified' 
                  ? 'bg-celadon-500 text-white' 
                  : 'text-gray-700 hover:bg-gray-50'
              }`}
            >
              Unified
            </button>
          </div>
        </div>

        <div className="flex items-center space-x-4">
          <label className="flex items-center space-x-2">
            <input
              type="checkbox"
              checked={showFormatted}
              onChange={(e) => setShowFormatted(e.target.checked)}
              className="rounded border-gray-300"
            />
            <span className="text-sm text-gray-700">Enhanced Formatting</span>
          </label>
        </div>
      </div>

      {selectedVersionObjects.length === 0 && (
        <div className="text-center py-8 text-gray-500">
          <Eye className="h-12 w-12 mx-auto mb-4 text-gray-300" />
          <p>Select versions to compare</p>
          <p className="text-sm">Choose 1-2 versions from the experiment to see their prompt differences</p>
        </div>
      )}

      {selectedVersionObjects.length === 1 && (
        <div className="space-y-4">
          {selectedVersionObjects.map((version) => {
            const performance = getPerformanceIndicator(version);
            return (
              <div key={version.id} className="border rounded-lg overflow-hidden">
                <div className={`p-4 border-l-4 ${getVersionColor(version)}`}>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-3">
                      <h3 className="text-lg font-semibold">{version.version_name}</h3>
                      {version.is_control && (
                        <span className="px-2 py-1 text-xs bg-celadon-100 text-celadon-700 rounded-full">Control</span>
                      )}
                      <span className={`text-sm ${performance.color}`}>
                        {performance.icon} {performance.label}
                      </span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <span className="text-sm text-gray-500">v{version.version_number}</span>
                      <button
                        onClick={() => handleCopy(version.content, version.id)}
                        className="p-2 text-gray-400 hover:text-gray-600 transition-colors"
                        title="Copy prompt"
                      >
                        {copiedStates[version.id] ? (
                          <Check className="h-4 w-4 text-celadon-600" />
                        ) : (
                          <Copy className="h-4 w-4" />
                        )}
                      </button>
                    </div>
                  </div>
                </div>
                <div className="p-4 bg-gray-50">
                  <pre className="whitespace-pre-wrap text-sm font-mono bg-white p-4 rounded border">
                    {formatPromptContent(version.content)}
                  </pre>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {selectedVersionObjects.length === 2 && viewMode === 'side-by-side' && (
        <div className="grid grid-cols-2 gap-4">
          {selectedVersionObjects.map((version, index) => {
            const performance = getPerformanceIndicator(version);
            return (
              <div key={version.id} className="border rounded-lg overflow-hidden">
                <div className={`p-4 border-l-4 ${getVersionColor(version)}`}>
                  <div className="flex items-center justify-between">
                    <div className="space-y-1">
                      <h3 className="font-semibold">{version.version_name}</h3>
                      {version.is_control && (
                        <span className="px-2 py-1 text-xs bg-celadon-100 text-celadon-700 rounded-full">Control</span>
                      )}
                      <div className="flex items-center space-x-2">
                        <span className={`text-xs ${performance.color}`}>
                          {performance.icon} {performance.label}
                        </span>
                      </div>
                    </div>
                    <button
                      onClick={() => handleCopy(version.content, version.id)}
                      className="p-2 text-gray-400 hover:text-gray-600 transition-colors"
                      title="Copy prompt"
                    >
                      {copiedStates[version.id] ? (
                        <Check className="h-4 w-4 text-celadon-600" />
                      ) : (
                        <Copy className="h-4 w-4" />
                      )}
                    </button>
                  </div>
                </div>
                <div className="p-4 bg-gray-50">
                  <pre className="whitespace-pre-wrap text-sm font-mono bg-white p-4 rounded border min-h-[200px]">
                    {formatPromptContent(version.content)}
                  </pre>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {selectedVersionObjects.length === 2 && viewMode === 'unified' && (
        <div className="border rounded-lg overflow-hidden">
          <div className="p-4 bg-gray-50 border-b">
            <div className="flex items-center justify-between">
              <h3 className="font-semibold">
                Comparing: {selectedVersionObjects[0].version_name} ↔ {selectedVersionObjects[1].version_name}
              </h3>
              <div className="text-sm text-gray-500">
                <span className="inline-block w-4 h-4 bg-gray-100 border border-gray-300 rounded mr-2"></span>
                Removed
                <span className="inline-block w-4 h-4 bg-celadon-100 border border-celadon-300 rounded mx-2 ml-4"></span>
                Added
                <span className="inline-block w-4 h-4 bg-gray-200 border border-gray-400 rounded mx-2 ml-4"></span>
                Modified
              </div>
            </div>
          </div>
          <div className="p-4">
            <div className="bg-white border rounded">
              {computeDiff(selectedVersionObjects[0].content, selectedVersionObjects[1].content).map((diff, index) => (
                <div key={index} className="font-mono text-sm">
                  {diff.type === 'equal' && (
                    <div className="px-4 py-1 border-l-4 border-gray-300">
                      <span className="text-gray-400 mr-4 inline-block w-8">{diff.lineNumber}</span>
                      {diff.content}
                    </div>
                  )}
                  {diff.type === 'removed' && (
                    <div className="px-4 py-1 bg-gray-50 border-l-4 border-gray-300">
                      <span className="text-gray-400 mr-4 inline-block w-8">{diff.lineNumber}</span>
                      <span className="text-gray-700">- {diff.content}</span>
                    </div>
                  )}
                  {diff.type === 'added' && (
                    <div className="px-4 py-1 bg-celadon-50 border-l-4 border-celadon-300">
                      <span className="text-gray-400 mr-4 inline-block w-8">{diff.lineNumber}</span>
                      <span className="text-celadon-700">+ {diff.content}</span>
                    </div>
                  )}
                  {diff.type === 'modified' && (
                    <>
                      <div className="px-4 py-1 bg-gray-50 border-l-4 border-gray-300">
                        <span className="text-gray-400 mr-4 inline-block w-8">{diff.lineNumber}</span>
                        <span className="text-gray-700">- {diff.content1}</span>
                      </div>
                      <div className="px-4 py-1 bg-celadon-50 border-l-4 border-celadon-300">
                        <span className="text-gray-400 mr-4 inline-block w-8">{diff.lineNumber}</span>
                        <span className="text-celadon-700">+ {diff.content2}</span>
                      </div>
                    </>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PromptDiffViewer; 